import { CircleAlertIcon as IconAlertCircle } from '@persona-id/design-tokens/icons';
import { DateTime } from 'luxon';
import { Helmet } from 'react-helmet-async';

import BankStatementVerification from 'dashboard/components/BankStatementVerification';
import InactiveEnvironmentBanner from 'dashboard/components/InactiveEnvironmentBanner';
import VerificationDetail from 'dashboard/components/VerificationDetail';
import Banner from 'dashboard/components-lib/Banner';
import LoadingIndicator from 'dashboard/components-lib/LoadingIndicator';
import NullState from 'dashboard/components-lib/NullState';
import { FlagIssuesFormData } from 'dashboard/forms/FormFlagIssues';
import {
  Backdrop,
  ColumnContainer,
  Container,
  HeaderContainer,
  MainColumn,
  RightColumn,
} from 'dashboard/layouts/ShowViewLayoutV2';
import { BankStatementDataField, DocumentGeneric } from 'dashboard/models/document';
import { VerificationState } from 'dashboard/redux/reducers/verification';
import VerificationHeader from 'dashboard/views/DashboardVerificationsShowView/VerificationHeader';
import VerificationInfo from 'dashboard/views/DashboardVerificationsShowView/VerificationInfo';
import { DATE_AND_TIME_FORMAT } from 'lib/constants/luxonFormats';
import useCurrentEnvironmentType from 'lib/hooks/useCurrentEnvironmentType';
import { ApiModel } from 'lib/interfaces/api';
import { FeatureFlagsMap } from 'lib/interfaces/feature-flags';

interface DashboardVerificationsShowViewProps {
  redactor: VerificationState['redactor'];
  verification: VerificationState['verification'];
  document: DocumentGeneric;
  isLoading: boolean;
  handleRedactVerification: (verificationId: string) => Promise<void>;
  handleFlagIssues: (values: FlagIssuesFormData) => Promise<void>;
  featureFlags: FeatureFlagsMap;
}

const DashboardVerificationsShowView = ({
  redactor,
  verification,
  document,
  isLoading,
  handleRedactVerification,
  handleFlagIssues,
  featureFlags,
}: DashboardVerificationsShowViewProps) => {
  const { type: environmentType } = useCurrentEnvironmentType();

  if (verification == null) {
    return isLoading ? (
      <Backdrop>
        <LoadingIndicator fullscreen={true} />
      </Backdrop>
    ) : (
      <Backdrop>
        <NullState
          message='We couldn’t find that Verification.'
          backRoute='/dashboard/verifications'
        />
      </Backdrop>
    );
  }

  const redactorName =
    redactor == null
      ? 'via API'
      : `by ${redactor.attributes['name-first']} ${redactor.attributes['name-last']}`;
  let redactionBannerText = null;
  if (verification.attributes['redacted-at'] != null) {
    redactionBannerText = `This verification was redacted ${redactorName} on ${DateTime.fromISO(
      verification.attributes['redacted-at'],
    ).toFormat(DATE_AND_TIME_FORMAT)}.`;
  } else if (verification.attributes['parent-marked-for-cascading-redaction-at'] != null) {
    redactionBannerText = `This verification was marked for redaction on ${DateTime.fromISO(
      verification.attributes['parent-marked-for-cascading-redaction-at'],
    ).toFormat(DATE_AND_TIME_FORMAT)}.`;
  }

  return (
    <>
      <Helmet title={`${verification.id} - Verifications`} />

      <InactiveEnvironmentBanner />
      {redactionBannerText && (
        <Banner data-test='verification-redaction-banner' icon={<IconAlertCircle />}>
          {redactionBannerText}
        </Banner>
      )}

      <Backdrop data-test='verification-show-view'>
        <Container>
          <HeaderContainer data-test='verification-show-view__header'>
            <VerificationHeader
              verification={verification}
              document={document}
              redactVerification={handleRedactVerification}
              handleFlagIssues={handleFlagIssues}
            />
          </HeaderContainer>
          <ColumnContainer>
            <MainColumn data-test='verification-details__main'>
              {featureFlags['documents:dashboard-show-credit-analysis'] &&
              verification.type === ApiModel.VerificationDocument &&
              BankStatementDataField in document.attributes.fields ? (
                <BankStatementVerification documents={[document]} verifications={[verification]} />
              ) : (
                <VerificationDetail
                  verification={verification}
                  documents={[document]}
                  featureFlags={featureFlags}
                  isDefaultOpen={true}
                  toggleable={false}
                  environmentType={environmentType}
                  handleFlagVerificationIssues={(_, values) => handleFlagIssues(values)}
                />
              )}
            </MainColumn>
            <RightColumn>
              <VerificationInfo verification={verification} />
            </RightColumn>
          </ColumnContainer>
        </Container>
      </Backdrop>
    </>
  );
};

export default DashboardVerificationsShowView;
